import React, { useCallback, useState, useEffect } from 'react';
import { cn } from '../../utils/cn';
import { useProjectPanelStore } from '../../store/projectPanelStore';
import { getAssetIcon, formatFileSize, formatRelativeTime } from '../../../core/types/asset.types';
import { useAssetUrl, isImageAssetType } from '../../hooks/useAssetUrl';

export const AssetPreview: React.FC = () => {
  const { assets, selectedAssetId, showPreview, togglePreview, deleteAsset, duplicateAsset } = useProjectPanelStore();
  const selectedAsset = selectedAssetId ? assets[selectedAssetId] : null;
  const [imageError, setImageError] = useState(false);

  // Reset error when asset changes
  useEffect(() => {
    setImageError(false);
  }, [selectedAssetId]);

  const shouldLoadPreview = selectedAsset && isImageAssetType(selectedAsset.type) && !imageError;
  const { url: previewUrl, loading: previewLoading } = useAssetUrl(
    shouldLoadPreview ? selectedAsset.path : null
  );

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  const handleCopyPath = useCallback(() => {
    if (selectedAsset) navigator.clipboard.writeText(selectedAsset.path);
  }, [selectedAsset]);

  const handleDelete = useCallback(() => {
    if (selectedAsset && confirm(`Delete "${selectedAsset.name}"?`)) deleteAsset(selectedAsset.id);
  }, [selectedAsset, deleteAsset]);

  const handleDuplicate = useCallback(() => {
    if (selectedAsset) duplicateAsset(selectedAsset.id);
  }, [selectedAsset, duplicateAsset]);

  if (!showPreview) {
    return <button onClick={togglePreview} className="absolute bottom-2 right-2 px-2 py-1 text-xs rounded bg-zinc-700 text-zinc-300 hover:bg-zinc-600">Show Preview</button>;
  }

  if (!selectedAsset) {
    return (
      <div className="w-56 flex-shrink-0 border-l border-zinc-700 bg-zinc-800 flex flex-col">
        <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-700">
          <span className="text-xs font-medium text-zinc-400">Preview</span>
          <button onClick={togglePreview} className="text-zinc-500 hover:text-zinc-300">×</button>
        </div>
        <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">
          <div className="text-center">
            <div className="text-2xl mb-2">📄</div>
            <div>Select an asset</div>
          </div>
        </div>
      </div>
    );
  }

  const icon = getAssetIcon(selectedAsset.type);
  const fileSize = formatFileSize(selectedAsset.metadata?.fileSize || 0);
  const modified = formatRelativeTime(new Date(selectedAsset.modifiedAt));
  const dimensions = selectedAsset.metadata?.width && selectedAsset.metadata?.height ? `${selectedAsset.metadata.width} × ${selectedAsset.metadata.height}` : null;

  return (
    <div className="w-56 flex-shrink-0 border-l border-zinc-700 bg-zinc-800 flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-700">
        <span className="text-xs font-medium text-zinc-300 truncate">{selectedAsset.name}</span>
        <button onClick={togglePreview} className="text-zinc-500 hover:text-zinc-300 text-lg leading-none">×</button>
      </div>
      <div className="p-4">
        <div className={cn(
          'aspect-square rounded flex items-center justify-center',
          isImageAssetType(selectedAsset.type)
            ? 'bg-checkerboard'
            : 'bg-gradient-to-br from-zinc-700 to-zinc-800'
        )}>
          {shouldLoadPreview && previewLoading ? (
            <div className="w-3/4 h-3/4 rounded bg-zinc-600 animate-pulse" />
          ) : previewUrl ? (
            <img
              src={previewUrl}
              alt={selectedAsset.name}
              className="max-w-full max-h-full object-contain"
              onError={handleImageError}
            />
          ) : (
            <span className="text-5xl">{icon}</span>
          )}
        </div>
      </div>
      <div className="px-4 pb-4 space-y-2 text-xs">
        <div className="flex justify-between"><span className="text-zinc-500">Type</span><span className="text-zinc-300">{selectedAsset.type}</span></div>
        <div className="flex justify-between"><span className="text-zinc-500">Size</span><span className="text-zinc-300">{fileSize}</span></div>
        {dimensions && <div className="flex justify-between"><span className="text-zinc-500">Dimensions</span><span className="text-zinc-300">{dimensions}</span></div>}
        <div className="flex justify-between"><span className="text-zinc-500">Modified</span><span className="text-zinc-300">{modified}</span></div>
      </div>
      <div className="px-4 pb-4">
        <div className="text-zinc-500 text-xs mb-1">Path</div>
        <div className="p-2 rounded bg-zinc-900 text-xs text-zinc-400 break-all select-all">{selectedAsset.path}</div>
      </div>
      <div className="mt-auto p-4 border-t border-zinc-700 space-y-2">
        <div className="flex gap-2">
          <button onClick={handleCopyPath} className="flex-1 px-2 py-1.5 text-xs rounded bg-zinc-700 text-zinc-300 hover:bg-zinc-600">📋 Copy Path</button>
          <button onClick={handleDuplicate} className="flex-1 px-2 py-1.5 text-xs rounded bg-zinc-700 text-zinc-300 hover:bg-zinc-600">📄 Duplicate</button>
        </div>
        <button onClick={handleDelete} className="w-full px-2 py-1.5 text-xs rounded bg-red-900/50 text-red-400 hover:bg-red-900/70">🗑️ Delete</button>
      </div>
    </div>
  );
};
